// skipcq: JS-W1028
import { useState, useEffect, useCallback } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { SPLAT_DEFINITIONS_COLLECTION } from '../utils/firestoreCollections';
import { aggregateAwardedByTypeFromDocs } from '../domain/splatStats';

/**
 * useSplatsTotal
 * Fetches all documents in `splatDefinitions` and returns:
 * - total Splats awarded (sum of `awardedCount`)
 * - per-type breakdown (sum of `awardedCount` by `type`, non-zero rows only, sorted by count desc)
 * Plus loading, error, and refresh.
 */
function useSplatsTotal() {
  const [totalSplatsAwarded, setTotalSplatsAwarded] = useState(0);
  const [awardedByType, setAwardedByType] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const splatsRef = collection(db, SPLAT_DEFINITIONS_COLLECTION);
      const snapshot = await getDocs(splatsRef);
      const { totalSplatsAwarded: nextTotal, awardedByType: nextByType } =
        aggregateAwardedByTypeFromDocs(snapshot.docs);
      setTotalSplatsAwarded(nextTotal);
      setAwardedByType(nextByType);
    } catch {
      setError('Failed to load splats');
      setTotalSplatsAwarded(0);
      setAwardedByType([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { totalSplatsAwarded, awardedByType, loading, error, refresh };
}

export default useSplatsTotal;
